"use client"

import { useEffect, useState } from "react"
import { X, Download, Share, Smartphone, CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isSafari, setIsSafari] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    console.log("[v0] [PWA] Starting PWA Install Prompt initialization")
    console.log("[v0] [PWA] User Agent:", navigator.userAgent)

    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    setIsIOS(iOS)
    console.log("[v0] [PWA] Is iOS:", iOS)

    const android = /Android/.test(navigator.userAgent)
    setIsAndroid(android)
    console.log("[v0] [PWA] Is Android:", android)

    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    setIsSafari(safari)
    console.log("[v0] [PWA] Is Safari:", safari)

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes("android-app://")
    setIsStandalone(standalone)
    console.log("[v0] [PWA] Is Standalone (already installed):", standalone)

    const dismissed = localStorage.getItem("pwa-install-dismissed")
    const dismissedTime = localStorage.getItem("pwa-install-dismissed-time")
    console.log("[v0] [PWA] Previously dismissed:", dismissed, "at", dismissedTime)

    const sevenDays = 7 * 24 * 60 * 60 * 1000
    const shouldShowAgain = dismissedTime ? Date.now() - Number.parseInt(dismissedTime) > sevenDays : true

    if (!standalone && (!dismissed || shouldShowAgain)) {
      console.log("[v0] [PWA] Will show prompt after 3 seconds")
      setTimeout(() => setShowPrompt(true), 3000)
    } else {
      console.log("[v0] [PWA] Skipping prompt - standalone:", standalone, "dismissed:", dismissed)
    }

    const handleBeforeInstall = (e: any) => {
      console.log("[v0] [PWA] ✅ beforeinstallprompt event captured! PWA is installable")
      e.preventDefault()
      setDeferredPrompt(e)
      if (!dismissed || shouldShowAgain) {
        setShowPrompt(true)
      }
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstall)
    console.log("[v0] [PWA] Listener added for beforeinstallprompt event")

    setTimeout(() => {
      console.log("[v0] [PWA] Status check after 3 seconds:")
      console.log("[v0] [PWA] - deferredPrompt ready:", !!deferredPrompt)
      console.log("[v0] [PWA] - Platform:", android ? "Android" : iOS ? "iOS" : "Other")
      if (!deferredPrompt && android) {
        console.log("[v0] [PWA] ⚠️ WARNING: beforeinstallprompt not fired on Android")
        console.log("[v0] [PWA] Possible reasons:")
        console.log("[v0] [PWA] 1. App already installed (check home screen)")
        console.log("[v0] [PWA] 2. Need more user engagement (wait 5+ seconds, click around)")
        console.log("[v0] [PWA] 3. PWA criteria not met (check manifest/service worker in DevTools)")
        console.log("[v0] [PWA] 4. Browser doesn't support (try Chrome)")
      }
    }, 3000)

    window.addEventListener("appinstalled", () => {
      console.log("[v0] [PWA] ✅ App installed successfully!")
      setShowSuccess(true)
      setTimeout(() => {
        setShowSuccess(false)
        setShowPrompt(false)
      }, 3000)
      localStorage.setItem("pwa-install-dismissed", "true")
      localStorage.setItem("pwa-install-dismissed-time", Date.now().toString())
    })

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall)
    }
  }, [])

  const handleInstall = async () => {
    console.log("[v0] [PWA] Install button clicked")
    console.log("[v0] [PWA] deferredPrompt state:", deferredPrompt)

    if (!deferredPrompt) {
      console.log("[v0] [PWA] ❌ Install prompt not ready yet")
      alert("Installation prompt is loading, please wait a moment and try again.")
      return
    }

    try {
      console.log("[v0] [PWA] Triggering install prompt...")
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      console.log("[v0] [PWA] User choice:", outcome)

      setDeferredPrompt(null)

      if (outcome === "accepted") {
        console.log("[v0] [PWA] ✅ User accepted installation")
        setShowSuccess(true)
        setTimeout(() => {
          setShowSuccess(false)
          setShowPrompt(false)
        }, 2000)
        localStorage.setItem("pwa-install-dismissed", "true")
        localStorage.setItem("pwa-install-dismissed-time", Date.now().toString())
      } else {
        console.log("[v0] [PWA] ❌ User dismissed installation")
      }
    } catch (error) {
      console.error("[v0] [PWA] Install prompt error:", error)
      alert("Installation failed. Please try using Chrome browser or check browser settings.")
    }
  }

  const handleDismiss = () => {
    console.log("[v0] [PWA] Prompt dismissed by user")
    setShowPrompt(false)
    localStorage.setItem("pwa-install-dismissed", "true")
    localStorage.setItem("pwa-install-dismissed-time", Date.now().toString())
  }

  if (!showPrompt || isStandalone) return null

  if (showSuccess) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-safe animate-in slide-in-from-bottom duration-300">
        <Card className="relative bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-2xl border-0">
          <div className="p-6">
            <div className="flex items-center gap-4">
              <CheckCircle2 className="h-10 w-10 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg">Successfully Installed!</h3>
                <p className="text-sm text-white/90">Bubble1 is now on your home screen</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 pb-safe animate-in slide-in-from-bottom duration-500">
      <Card className="relative bg-white shadow-2xl border-0 overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500" />

        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>

        <div className="p-5 pr-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="h-9 w-9 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-xl text-gray-900 mb-1">Install Bubble1</h3>
              <p className="text-sm text-gray-600">Add to home screen for quick access</p>
            </div>
          </div>

          {isIOS && isSafari ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Add to your home screen for the full app experience:</p>
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  Tap <Share className="inline h-4 w-4" /> Share button below
                </li>
                <li>Scroll and tap &quot;Add to Home Screen&quot;</li>
                <li>Tap &quot;Add&quot; to confirm</li>
              </ol>
              <div className="mt-3 space-y-1.5">
                <p className="text-xs text-gray-600 font-semibold">Benefits:</p>
                <ul className="text-xs text-gray-500 space-y-1 pl-3">
                  <li>• Works like a native app</li>
                  <li>• No browser bars</li>
                  <li>• Faster access</li>
                  <li>• Push notifications</li>
                </ul>
              </div>
            </div>
          ) : isIOS && !isSafari ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Open this link in Safari to install Bubble1 as an app.</p>
              <p className="text-xs text-gray-500 bg-gray-100 p-2 rounded">Copy the URL and open in Safari browser</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center p-2 bg-cyan-50 rounded-lg">
                  <div className="text-xl mb-1">⚡</div>
                  <p className="text-xs text-gray-700 font-medium">Fast</p>
                </div>
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <div className="text-xl mb-1">📱</div>
                  <p className="text-xs text-gray-700 font-medium">Native</p>
                </div>
                <div className="text-center p-2 bg-cyan-50 rounded-lg">
                  <div className="text-xl mb-1">🔔</div>
                  <p className="text-xs text-gray-700 font-medium">Notifications</p>
                </div>
              </div>

              <Button
                onClick={handleInstall}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-6 text-base shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Download className="h-5 w-5 mr-2" />
                {deferredPrompt ? "Install App Now" : "Loading Install..."}
              </Button>

              <p className="text-xs text-center text-gray-500">
                {deferredPrompt ? "Free • Instant • No app store needed" : "Preparing installation..."}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
